import React, { useState, useMemo } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  InputAdornment,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useTranslation } from 'react-i18next';
import TimeMaskInput from 'common/MaskInputs/TimeMaskInput';
import generateTimeOptions from '../generateTimeOptions';
import dateSelectHelpers from '../dateSelectHelpers';

interface DateFromSelectProps {
  onDateChange: (date: Date) => void;
  date: Date | null;
  inputRef: React.MutableRefObject<HTMLInputElement | null>
}

const minutesEarlierStart = 30;

export default function DateFromSelect({
  date,
  onDateChange,
  inputRef,
}: DateFromSelectProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const options = useMemo(
    () =>
      generateTimeOptions({
        hours: date?.getHours(),
        minutes: date ? date.getMinutes() - minutesEarlierStart : 0,
      }),
    [date],
  );

  const { handleOptionChange, handleInputChange, convertDateToTimeString } =
    dateSelectHelpers({
      onDateChange,
      date,
      setIsOpen,
      inputRef,
    });

  return (
    <Box sx={{ width: '50%' }}>
      <Autocomplete
        freeSolo
        filterOptions={(option) => option}
        open={isOpen}
        sx={{
          '& .MuiInputBase-root.MuiOutlinedInput-root': {
            paddingRight: '1.6rem',
          },
        }}
        value={date ? convertDateToTimeString(date) : null}
        onChange={handleOptionChange}
        options={options}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option}>
            <Typography variant="menuItemDefault">{option}</Typography>
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('label.time_start')}
            onChange={handleInputChange}
            InputProps={{
              ...params.InputProps,
              inputComponent: TimeMaskInput as any,
              endAdornment: (
                <InputAdornment position="end">
                  <AccessTimeIcon
                    sx={{ fontSize: '2rem', color: 'icon.dark' }}
                  />
                </InputAdornment>
              ),
            }}
            onFocus={() => setIsOpen(true)}
            onBlur={() => setIsOpen(false)}
            inputRef={inputRef}
          />
        )}
      />
    </Box>
  );
}
